import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { api, ErroDaApi, ORIGEM_DA_API, type Conta, type EstadoDaSincronizacao } from './api';

/**
 * Eventos em tempo real (ADR 0053): o servidor avisa quando a caixa mudou, e
 * a tela invalida o cache em vez de esperar o polling.
 *
 * O `EventSource` nao manda cabecalho, entao o Bearer nao vai junto: antes de
 * abrir, pede um ticket de uso unico com o token (que continua so em memoria,
 * Secao 15.3) e abre o fluxo com ele. Ticket usado nao serve de novo, e por
 * isso a reconexao automatica do proprio `EventSource` nao ajuda - quem
 * reconecta e este modulo, com ticket novo e espera crescente.
 *
 * Enquanto nao ha fluxo, nada muda: a lista continua no polling de 30 s.
 */

const ESPERAS_MS = [1_000, 2_000, 5_000, 10_000, 30_000];
/** Servidor sem a rota (versao anterior): tenta de novo bem mais tarde. */
const SEM_ROTA_MS = 5 * 60_000;
/** Avisos que chegam juntos (varias mensagens, estado e mudanca) viram uma ida. */
const AGRUPAR_MS = 300;

let conectado = false;

/** Ha fluxo aberto agora. O polling da lista consulta isto a cada volta. */
export function tempoRealConectado(): boolean {
  return conectado;
}

interface DadosDoEstado {
  accountId: string;
  estado: EstadoDaSincronizacao;
  lastSyncAt: string | null;
  mudou: boolean;
}

export interface DepsDoTempoReal {
  criarFonte?: (url: string) => EventSource;
  pedirTicket?: () => Promise<string>;
  online?: () => boolean;
  aleatorio?: () => number;
  /** Lembrete de acompanhamento venceu sem resposta (WYM-15): a tela avisa. */
  aoLembrete?: () => void;
}

export function conectarTempoReal(cliente: QueryClient, deps: DepsDoTempoReal = {}): () => void {
  const {
    criarFonte = typeof EventSource === 'undefined' ? undefined : (url) => new EventSource(url),
    pedirTicket = async () =>
      (await api<{ ticket: string }>('/events/ticket', { method: 'POST' })).ticket,
    online = () => navigator.onLine,
    aleatorio = Math.random,
    aoLembrete,
  } = deps;

  // Navegador sem EventSource: fica no polling, que e o que ja existia.
  if (criarFonte === undefined) return () => undefined;

  let ativo = true;
  let fonte: EventSource | null = null;
  let falhas = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let agrupando: ReturnType<typeof setTimeout> | undefined;

  function invalidar(): void {
    if (agrupando !== undefined) return;
    agrupando = setTimeout(() => {
      agrupando = undefined;
      void cliente.invalidateQueries({ queryKey: ['conversas'] });
      void cliente.invalidateQueries({ queryKey: ['pastas'] });
      // Mensagem nova pode ter cancelado um lembrete (resposta detectada).
      void cliente.invalidateQueries({ queryKey: ['lembretes'] });
    }, AGRUPAR_MS);
  }

  /** Ultimo estado fora de "sincronizando", por conta. */
  const assentados = new Map<string, EstadoDaSincronizacao>();

  function atualizarConta(dados: DadosDoEstado): void {
    let anterior = assentados.get(dados.accountId);
    cliente.setQueryData<Conta[]>(['contas'], (contas) =>
      contas?.map((c) => {
        if (c.id !== dados.accountId) return c;
        if (anterior === undefined && c.syncStatus !== 'sincronizando') anterior = c.syncStatus;
        return { ...c, syncStatus: dados.estado, lastSyncAt: dados.lastSyncAt ?? c.lastSyncAt };
      }),
    );
    if (dados.estado === 'sincronizando') return;
    assentados.set(dados.accountId, dados.estado);
    // Saiu de erro, ou entrou: o resto da conta (o `syncState` que o menu
    // mostra) vem do servidor.
    if (anterior !== undefined && anterior !== dados.estado) {
      void cliente.invalidateQueries({ queryKey: ['contas'] });
    }
  }

  function fechar(): void {
    fonte?.close();
    fonte = null;
    if (conectado) {
      conectado = false;
      // A proxima ida ja agendada pode ser a de 5 min; esta volta a lista ao
      // ritmo do polling na hora.
      invalidar();
    }
  }

  function agendar(espera?: number): void {
    clearTimeout(timer);
    const degrau = ESPERAS_MS[Math.min(falhas, ESPERAS_MS.length - 1)]!;
    const ms = espera ?? Math.round(degrau / 2 + aleatorio() * (degrau / 2));
    timer = setTimeout(() => void abrir(), ms);
  }

  async function abrir(): Promise<void> {
    if (!ativo) return;
    if (!online()) {
      agendar();
      return;
    }

    let ticket: string;
    try {
      ticket = await pedirTicket();
    } catch (erro) {
      if (!ativo) return;
      falhas++;
      agendar(erro instanceof ErroDaApi && erro.status === 404 ? SEM_ROTA_MS : undefined);
      return;
    }
    if (!ativo) return;

    const f = criarFonte!(`${ORIGEM_DA_API}/api/v1/events?ticket=${encodeURIComponent(ticket)}`);
    fonte = f;

    f.onopen = () => {
      falhas = 0;
      conectado = true;
      // O que chegou enquanto o fluxo estava fechado nao vira evento.
      invalidar();
    };
    f.addEventListener('mail.novo', invalidar);
    f.addEventListener('lembrete.vencido', () => {
      void cliente.invalidateQueries({ queryKey: ['lembretes'] });
      aoLembrete?.();
    });
    f.addEventListener('sync.estado', (evento) => {
      try {
        const dados = JSON.parse((evento as MessageEvent<string>).data) as DadosDoEstado;
        atualizarConta(dados);
        if (dados.mudou) invalidar();
      } catch {
        // Evento malformado nao derruba o fluxo.
      }
    });
    // O servidor encerra o fluxo na vida do access token; renovar e rotina.
    f.addEventListener('renovar', () => {
      fechar();
      falhas = 0;
      agendar(0);
    });
    f.onerror = () => {
      if (fonte !== f) return;
      fechar();
      falhas++;
      agendar();
    };
  }

  const aoVoltarRede = () => {
    if (fonte === null) agendar(0);
  };
  window.addEventListener('online', aoVoltarRede);
  void abrir();

  return () => {
    ativo = false;
    clearTimeout(timer);
    clearTimeout(agrupando);
    window.removeEventListener('online', aoVoltarRede);
    fechar();
  };
}

/** Liga os eventos enquanto o shell esta montado; sair da conta desliga. */
export function useTempoReal(aoLembrete?: () => void): void {
  const cliente = useQueryClient();
  // Em ref: trocar o callback a cada render nao pode reabrir o fluxo.
  const aviso = useRef(aoLembrete);
  useEffect(() => {
    aviso.current = aoLembrete;
  });
  useEffect(() => conectarTempoReal(cliente, { aoLembrete: () => aviso.current?.() }), [cliente]);
}
